import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import CameraSlot from "orion-components/SharedComponents/CameraClusters/CameraSlot";

const widthGap = 10;
const heightGap = 10;
const ASPECT = 16 / 9;
const MAX_GRID_HEIGHT_RATIO = 0.9; // As a percentage of viewport height

// Calculate grid and by extension CameraSlot dimensions based on number of rows and columns
// fitting into a max grid height of 90% of viewport height
const ResizableGrid = ({ rows, columns, cameras, isOnAlertProfile, alertDateTime }) => {
	const [gridTemplateRows, setGridTemplateRows] = useState(`repeat(${rows}, 1fr)`);
	const [gridTemplateColumns, setGridTemplateColumns] = useState(`repeat(${columns}, 1fr)`);
	const [gridItemHeight, setGridItemHeight] = useState("100%");
	const [gridItemWidth, setGridItemWidth] = useState("100%");
	const [items, setItems] = useState([]);
	const [containerHeight, setContainerHeight] = useState("100%");

	const gridContainerRef = useRef(null);

	// Prevent feedback loops / duplicates in StrictMode
	const lastDimsRef = useRef({ itemH: null, itemW: null, contH: null });
	const lastContainerWidthRef = useRef(0);
	const roRef = useRef(null);
	const rafIdRef = useRef(0);

	const scheduleCalc = useCallback(() => {
		if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
		rafIdRef.current = requestAnimationFrame(() => {
			rafIdRef.current = 0;
			calculateGridToFit();
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const calculateGridToFit = useCallback(() => {
		const r = Math.max(1, Number(rows) || 1);
		const c = Math.max(1, Number(columns) || 1);

		const containerEl = gridContainerRef.current;
		const viewportH = (window.visualViewport?.height ?? window.innerHeight) - 1; // avoid scrollbar ping-pong
		if (!containerEl || !viewportH) return;

		// Always fill available width, compute tiles from that width
		const containerClientWidth = Math.round(containerEl.clientWidth);
		if (!containerClientWidth) return;

		const verticalGaps = r > 1 ? heightGap * (r - 1) : 0;
		const horizontalGaps = c > 1 ? widthGap * (c - 1) : 0;

		// 90% of viewport height cap
		const maxGridHeight = Math.floor(viewportH * MAX_GRID_HEIGHT_RATIO);
		const heightBudgetForTiles = Math.max(0, maxGridHeight - verticalGaps);
		const widthBudgetForTiles = Math.max(0, containerClientWidth - horizontalGaps);

		// Per-slot constraints (integer math for stability)
		const slotHeightLimitByHeight = Math.floor(heightBudgetForTiles / r);
		const slotWidthLimitByWidth = Math.floor(widthBudgetForTiles / c);
		const slotHeightFromWidth = Math.floor(slotWidthLimitByWidth / ASPECT);

		const slotHeight = Math.max(0, Math.min(slotHeightLimitByHeight, slotHeightFromWidth));
		const slotWidth = slotWidthLimitByWidth;

		const totalHeight = slotHeight * r + verticalGaps;

		const next = {
			itemH: slotHeight,
			itemW: slotWidth,
			contH: totalHeight
		};

		const prev = lastDimsRef.current;
		if (prev.itemH === next.itemH && prev.itemW === next.itemW && prev.contH === next.contH) return;

		lastDimsRef.current = next;
		setGridItemHeight(next.itemH);
		setGridItemWidth(next.itemW);
		setContainerHeight(next.contH);
	}, [rows, columns]);

	useEffect(() => {
		// rows/cols change -> rebuild slots and recalc
		setGridTemplateRows(`repeat(${rows}, 1fr)`);
		setGridTemplateColumns(`repeat(${columns}, 1fr)`);
		const r = Math.max(1, Number(rows) || 1);
		const c = Math.max(1, Number(columns) || 1);
		const newItems = [];
		for (let i = 0; i < r * c; i++) newItems.push({ currentRow: Math.floor(i / c), currentCol: i % c });
		setItems(newItems);
		scheduleCalc();
	}, [rows, columns, scheduleCalc]);

	useEffect(() => {
		const onWinResize = () => scheduleCalc();

		// Ensure a single observer in StrictMode
		if (roRef.current) {
			try {
				if (gridContainerRef.current) roRef.current.unobserve(gridContainerRef.current);
				roRef.current.disconnect();
			} catch {}
		}
		roRef.current = new ResizeObserver(entries => {
			const entry = entries[0];
			const w = Math.round(entry?.contentRect?.width || 0);
			// Only react to width changes and ignore tiny deltas (<=1px)
			if (w && Math.abs(w - lastContainerWidthRef.current) > 1) {
				lastContainerWidthRef.current = w;
				scheduleCalc();
			}
		});
		if (gridContainerRef.current) {
			lastContainerWidthRef.current = Math.round(gridContainerRef.current.clientWidth || 0);
			roRef.current.observe(gridContainerRef.current);
		}

		window.addEventListener("resize", onWinResize);
		return () => {
			window.removeEventListener("resize", onWinResize);
			if (roRef.current) {
				try {
					if (gridContainerRef.current) roRef.current.unobserve(gridContainerRef.current);
					roRef.current.disconnect();
				} catch {}
				roRef.current = null;
			}
			if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
		};
	}, [scheduleCalc]);

	return (
		<div
			style={{
				width: "100%",
				marginLeft: "0.4%",
				marginRight: "0.1%",
				overflow: "hidden"
			}}
			ref={gridContainerRef}
		>
			<div
				className="camera-grid-container"
				style={{
					gridTemplateRows,
					gridTemplateColumns,
					width: "100%", // always fill max available width
					height: containerHeight
				}}
			>
				{items.map((item, index) => (
					<div
						key={index}
						className="camera-grid-item"
						style={{ height: gridItemHeight, width: gridItemWidth }}
					>
						<div className="camera-grid-item-content">
							<CameraSlot
								rows={rows}
								columns={columns}
								currentCol={item.currentCol}
								currentRow={item.currentRow}
								index={index}
								key={index}
								selectedClusterId={item}
								selectedClusterCameras={cameras}
								isOnAlertProfile={isOnAlertProfile}
								alertDateTime={alertDateTime}
								isOnProfile={true}
							/>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

ResizableGrid.propTypes = {
	rows: PropTypes.any,
	columns: PropTypes.any,
	cameras: PropTypes.array,
	isOnAlertProfile: PropTypes.bool
};

ResizableGrid.defaultProps = {
	isOnAlertProfile: false
};

export default ResizableGrid;
